import React, { useState, useMemo, useRef } from "react";
import StoreSearch from "../../../Campaign/StoreSearch";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Chip,
  Box,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PdfIcon from "../../../../../shared/assets/icons/PdfIcon.png";
import styles from "../../JobBatch.module.css";
import axios from "axios";
import { useParams } from "react-router-dom";
import moment from "moment";
import ModalToUpdateClusters from "../../ModalToUpdateClusters";

// Helper to get job status (Complete/Pending) based on cluster serviceType
const getJobStatus = (job, serviceType) => {
  if (!job) return "Pending";
  if (serviceType === "3") {
    return job.paintingJobRelatedFields?.paintingJobCompleted === true
      ? "Complete"
      : "Pending";
  }
};

const AllClusters = ({ setTotalClusters }) => {
  const [openModalToUpdateCluster, setOpenModalToUpdateCluster] =
    React.useState(false);
  const [detailsOfClusterSelected, setDetailsOfClusterSelected] =
    React.useState();
  const [expandedCluster, setExpandedCluster] = React.useState(null);
  const [expandedBatch, setExpandedBatch] = React.useState(null);
  const { id } = useParams();
  const [clusters, setClusters] = React.useState([]);
  const [clusterLoading, setClusterLoading] = useState(false);
  const [error, setError] = useState(null);
  const batchRefs = useRef({});
  const containerRef = useRef(null);

  const searchData = useMemo(() => {
    const result = [];

    clusters.forEach((cluster) => {
      cluster.batches?.forEach((batch) => {
        batch.jobs?.forEach((job) => {
          result.push({
            id: job._id,
            storeName: job.storeName,

            clusterTitle: cluster.clusterTitle,

            batchTitle: batch.batchTitle,

            targetId: batch._id,

            subText: `${cluster.clusterTitle} → ${batch.batchTitle}`,
          });
        });
      });
    });

    return result;
  }, [clusters]);

  const handleAccordionToggle = (clusterTitle, batchTitle) => (event) => {
    event.stopPropagation();
    if (clusterTitle) {
      setExpandedCluster((prev) =>
        prev === clusterTitle ? null : clusterTitle,
      );
    }
    if (batchTitle) {
      setExpandedBatch((prev) => (prev === batchTitle ? null : batchTitle));
    }
  };

  React.useEffect(() => {
    if (id) {
      fetchAllClustersBasedOnCampaignId(id);
    }
  }, [id]);

  // Calculate total jobs in a cluster with proper null checks
  const calculateTotalJobsInCluster = (cluster) => {
    if (!cluster || !cluster.batches) return 0;
    return cluster.batches.reduce((total, batch) => {
      return total + (batch?.jobs?.length || 0);
    }, 0);
  };

  // fetch clusters with proper null checks
  const fetchAllClustersBasedOnCampaignId = async (id) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/vendorClusterBatches/getAllClusterBatches?campaignId=${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );
      if (response?.data?.success) {
        const updatedClusters = await response?.data?.data.map((cluster) => ({
          ...cluster,
          batches: cluster.batches || [], // Ensure batches exists
        }));
        setTotalClusters(response?.data?.data?.length || 0);

        // Filter out clusters with no batches
        const filteredClusters = updatedClusters.filter(
          (cluster) => cluster.batches && cluster.batches.length > 0,
        );

        // Sort clusters by createdAt in descending order (newest first)
        const sortedClusters = filteredClusters.sort((a, b) => {
          return new Date(b.createdAt) - new Date(a.createdAt);
        });

        setClusters(sortedClusters);
        setClusterLoading(false);
      } else {
        setError(
          "Failed to fetch clusters: " +
            (response?.data?.message || "Unknown error"),
        );
        setClusterLoading(false);
      }
    } catch (error) {
      console.error("Error while fetching clusters", error);
      setError("Error fetching clusters: " + error.message);
      setClusterLoading(false);
    }
  };
  // Update cluster in the list
  const updateClusterInList = (updatedCluster) => {
    setClusters((prevClusters) =>
      prevClusters.map((cluster) =>
        cluster._id === updatedCluster._id ? updatedCluster : cluster,
      ),
    );
    // Also update the details if this is the currently selected cluster
    if (detailsOfClusterSelected?._id === updatedCluster._id) {
      setDetailsOfClusterSelected(updatedCluster);
    }
  };

  // DataRow now conditionally renders Job Status column only when serviceType === "3"
  const DataRow = ({ row, serviceType }) => {
    const jobStatus = getJobStatus(row, serviceType);
    const showJobStatus = serviceType === "3";
    return (
      <TableRow>
        <TableCell className={styles.tableCell}>
          <div className={styles.sharedTextStyle}>{row?.storeName}</div>
          <div
            className={styles.address2}
          >{`${row?.address},${row?.city},${row?.state}-${row?.zipCode}`}</div>
        </TableCell>
        <TableCell className={styles.tableCell}>
          <div className={styles.sharedTextStyle}>{row?.storeOwnerName}</div>
          <div className={styles.storeManagerContact}>
            {row?.storeOwnerPhoneNumber}
          </div>
        </TableCell>
        <TableCell className={styles.tableCell}>
          <div className={styles.sharedTextStyle}>
            {`${
              row?.productId?.approvedWidth ?? row?.productId?.productWidth
            } * ${
              row?.productId?.approvedHeight ?? row?.productId?.productHeight
            } * ${row?.productId?.productThickness ?? "Not Available"}`}

            {/* <TableCell className={styles.tableCell}>
        <div className={styles.sharedTextStyle}>
          {row?.baseMaterial || "N/A"}
        </div>
      </TableCell> */}
            {/* <TableCell className={styles.tableCell}>
        <div className={styles.sharedTextStyle}>{row?.printDetails}</div>
      </TableCell> */}
            {/* <TableCell className={styles.tableCell}>
        {row?.product?.printImage ? (
          <div className={styles.iconLinkContainer}>
            <a
              href={row.product.printImage}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.iconLink}
            >
              <div className={styles.pdfIconWithText}>
                <img
                  src="/assests/AdminDashboard/pdfImage.png"
                  alt="Print File"
                  className={styles.pdfIcon}
                />
              </div>
            </a>
          </div>
        ) : (
          <div className={styles.sharedTextStyle}>No print file</div>
        )}
      </TableCell> */}
            {/* <TableCell className={styles.tableCell}>
        {row?.materialDetailsForFabrication?.cadFile ? (
          <div className={styles.iconLinkContainer}>
            <a
              href={row.materialDetailsForFabrication.cadFile}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.iconLink}
            >
              <div className={styles.pdfIconWithText}>
                <img
                  src="/assests/AdminDashboard/pdfImage.png"
                  alt="CAD File"
                  className={styles.pdfIcon}
                />
              </div>
            </a>
          </div>
        ) : (
          <div className={styles.sharedTextStyle}>No CAD file</div>
        )}
      </TableCell> */}
          </div>
        </TableCell>
        <TableCell className={styles.tableCell}>
          <div className={styles.sharedTextStyle}>
            {row?.productId?.productType || "N/A"}
          </div>
        </TableCell>
        {/* NEW COLUMN: JOB STATUS */}
        {showJobStatus && (
          <TableCell className={styles.tableCell}>
            <Chip
              label={jobStatus}
              className={
                jobStatus === "Complete"
                  ? `${styles.amountPaid} !w-[100px] !flex !justify-center`
                  : `${styles.pending} !w-[100px] !flex !justify-center`
              }
              sx={{
                "& .MuiChip-label": {
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                },
              }}
            />
          </TableCell>
        )}
      </TableRow>
    );
  };

  return (
    <Grid ref={containerRef} className={`${styles.scrollableContainer}`}>
      <div className="mx-3 py-2 sticky top-0 z-20 bg-white">
        <StoreSearch
          data={searchData}
          searchKey="storeName"
          placeholder="Search Store"
          containerRef={containerRef}
          targetRefs={batchRefs}
          onSelect={(item) => {
            setExpandedCluster(item.clusterTitle);

            setExpandedBatch(item.batchTitle);
          }}
        />
      </div>
      {error && (
        <Box className={styles.errorBox}>
          <p className={styles.errorText}>{error}</p>
        </Box>
      )}
      {clusterLoading ? (
        <div className={styles.noJobsStyle}>
          <CircularProgress />
        </div>
      ) : clusters.length === 0 ? (
        <div className={styles.noJobsStyle}>
          <Box className={styles.noJobsBox}>
            <p className={styles.noJobsText}>
              No clusters of batches available
            </p>
            {error && <p className={styles.errorText}>{error}</p>}
          </Box>
        </div>
      ) : (
        clusters.map((cluster, index) => {
          const clusterServiceType = String(cluster.serviceType);
          const showJobStatusColumn = clusterServiceType === "3";
          return (
            <Grid item xs={12} md={12} key={index} my={1} mx={2}>
              <Accordion expanded={expandedCluster === cluster?.clusterTitle}>
                <AccordionSummary
                  expandIcon={
                    <IconButton
                      onClick={handleAccordionToggle(cluster?.clusterTitle)}
                    >
                      <ExpandMoreIcon />
                    </IconButton>
                  }
                  aria-controls={`panel-${index}-content`}
                  id={`panel-${index}-header`}
                  className={styles.accordionHeader}
                >
                  <div className={styles.batchHeader}>
                    <div className={styles.clusterTitle}>
                      {cluster?.clusterTitle}
                    </div>
                    <div
                      className={`${styles.chipContainer} flex items-center gap-2`}
                    >
                      {/* Product Type Chip */}
                      <Tooltip
                        title={
                          cluster.productType && cluster.productType.length > 15
                            ? cluster.productType
                            : ""
                        }
                        disableHoverListener={
                          !(
                            cluster.productType &&
                            cluster.productType.length > 15
                          )
                        }
                      >
                        <Chip
                          label={
                            cluster.productType?.length > 15
                              ? `${cluster.productType.slice(0, 15)}...`
                              : cluster.productType || "N/A"
                          }
                          className={`${styles.chip} !px-2 !flex !justify-center`}
                          sx={{
                            "& .MuiChip-label": {
                              display: "flex",
                              justifyContent: "center",
                              width: "100%",
                              padding: 0,
                            },
                          }}
                        />
                      </Tooltip>

                      <Chip
                        label={`Batches: ${cluster.batches?.length || 0}`}
                        className={`${styles.chip} !w-[100px] !flex !justify-center`}
                        sx={{
                          "& .MuiChip-label": {
                            display: "flex",
                            justifyContent: "center",
                            width: "100%",
                            padding: 0,
                          },
                        }}
                      />
                      {/* Jobs Chip */}
                      <Chip
                        label={`Jobs: ${calculateTotalJobsInCluster(cluster)}`}
                        className={`${styles.chip} !w-[80px] !flex !justify-center`}
                        sx={{
                          "& .MuiChip-label": {
                            display: "flex",
                            justifyContent: "center",
                            width: "100%",
                            padding: 0,
                          },
                        }}
                      />
                      {/* Date Range Chip */}
                      <Chip
                        label={`Timeline: ${moment(
                          cluster?.jobStartingDate,
                        ).format("DD MMM YY")} - ${moment(
                          cluster?.jobCompletionDate,
                        ).format("DD MMM YY")}`}
                        className={styles.chip}
                      />
                      {/* <Chip
                      label={
                        <div className={styles.linkWithImage}>
                          <img
                            src={OrgName}
                            alt="OrgName Icon"
                            className={styles.icon}
                          />
                          {cluster?.organisationName}
                        </div>
                      }
                      className={styles.chip}
                    /> */}
                      {/* <Chip
                      label={
                        <div className={styles.linkWithImage}>
                          <img
                            src={VendorIcon}
                            alt="Vendor Name"
                            className={styles.icon}
                          />
                          {cluster?.userContact}
                        </div>
                      }
                      className={styles.chip}
                    /> */}
                      {/* 
                    <Chip
                      label={`Advance Paid: ${cluster?.advancePaid || 0}`}
                      className={styles.approved}
                    /> */}
                      {/* Status Chip */}
                      <Chip
                        label={
                          cluster.batches?.every((batch) => batch.status === 2)
                            ? "Complete"
                            : `Pending: ${
                                cluster?.batches?.filter(
                                  (batch) => batch.status !== 2,
                                ).length || 0
                              }`
                        }
                        className={
                          cluster.batches?.every((batch) => batch.status === 2)
                            ? `${styles.amountPaid} !w-[100px] !flex !justify-center`
                            : `${styles.pending} !w-[100px] !flex !justify-center`
                        }
                        sx={{
                          "& .MuiChip-label": {
                            display: "flex",
                            justifyContent: "center",
                            width: "100%",
                          },
                        }}
                      />
                      {/* Options Button */}
                      <button
                        className="text-gray-400 hover:text-gray-600 text-xl rounded-full w-8 h-8 flex items-center justify-center hover:bg-gray-100"
                        onClick={() => {
                          setOpenModalToUpdateCluster(true);
                          setDetailsOfClusterSelected(cluster);
                        }}
                      >
                        ⋮
                      </button>
                    </div>
                  </div>
                </AccordionSummary>
                <AccordionDetails className={styles.accordionDetails}>
                  <Box>
                    {cluster?.batches?.map((batch, batchIndex) => (
                      <div
                        ref={(el) => {
                          batchRefs.current[batch._id] = el;
                        }}
                      >
                        <Accordion
                          expanded={expandedBatch === batch?.batchTitle}
                          key={batchIndex}
                          className={`${styles.accordionCustom}`}
                        >
                          <AccordionSummary
                            expandIcon={
                              <IconButton
                                onClick={handleAccordionToggle(
                                  null,
                                  batch?.batchTitle,
                                )}
                              >
                                <ExpandMoreIcon />
                              </IconButton>
                            }
                            aria-controls={`panel-${batchIndex}-content`}
                            id={`panel-${batchIndex}-header`}
                          >
                            <div className={styles.batchHeader}>
                              <h1 className={styles.batchTitle}>
                                {batch?.batchTitle}
                              </h1>
                              <div className={styles.chipContainer}>
                                {/* <Chip
                              label={batch?.status || "PDI Status: Not Requested"}
                            /> */}
                                {/* Added jobs count for each batch */}
                                <Chip
                                  label={`Jobs: ${batch.jobs?.length || 0}`}
                                  className={styles.chip}
                                />
                              </div>
                            </div>
                          </AccordionSummary>
                          <AccordionDetails className={styles.accordionDetails}>
                            <Box className={`${styles.scrollableBox}`}>
                              <TableContainer
                                component={Paper}
                                className={styles.tableContainer}
                              >
                                <Table>
                                  <TableHead>
                                    <TableRow className={styles.tableHeader}>
                                      <TableCell className={styles.tableCell}>
                                        DELIVERY ADDRESS
                                      </TableCell>
                                      <TableCell className={styles.tableCell}>
                                        STORE MANAGER
                                      </TableCell>
                                      <TableCell className={styles.tableCell}>
                                        DIMENSIONS (W×H×T)
                                      </TableCell>
                                      {/* <TableCell className={styles.tableCell}>
                                      BASE MATERIAL
                                    </TableCell> */}
                                      {/* <TableCell className={styles.tableCell}>
                                    PRINT DETAILS
                                  </TableCell> */}
                                      {/* <TableCell className={styles.tableCell}>
                                      PRINT IMAGE
                                    </TableCell> */}
                                      {/* <TableCell className={styles.tableCell}>
                                      CAD FILE
                                    </TableCell> */}
                                      <TableCell className={styles.tableCell}>
                                        PRODUCT TYPE
                                      </TableCell>
                                      {/* <TableCell className={styles.tableCell}>
                                    OEM PREFERENCE
                                  </TableCell> */}
                                      {showJobStatusColumn && (
                                        <TableCell className={styles.tableCell}>
                                          JOB STATUS
                                        </TableCell>
                                      )}
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {batch?.jobs?.map((row, rowIndex) => (
                                      <DataRow
                                        key={rowIndex}
                                        row={row}
                                        serviceType={clusterServiceType}
                                      />
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      </div>
                    ))}
                  </Box>
                </AccordionDetails>
                {/* <BatchActions /> */}
              </Accordion>
            </Grid>
          );
        })
      )}
      {openModalToUpdateCluster && (
        <ModalToUpdateClusters
          openModal={openModalToUpdateCluster}
          setOpenModalToUpdateCluster={setOpenModalToUpdateCluster}
          cluster={detailsOfClusterSelected}
          onUpdate={updateClusterInList}
          fetchAllClustersBasedOnCampaignId={fetchAllClustersBasedOnCampaignId}
        />
      )}
    </Grid>
  );
};

export default AllClusters;
